/**
 * Guide PDF — Procédure tournée matin pour opérateurs terrain.
 * Phase 64. Imprimable A4 noir & blanc (économie d'encre couleur).
 *
 * Cible : 3 collaborateurs qui font la tournée des distributeurs/revendeurs
 * sans accès à un PC. Ce guide est leur "fiche de poche" papier ou tablette.
 *
 * Génération : via @react-pdf/renderer + bouton "Télécharger le guide PDF"
 * depuis Paramètres.
 */
import React from "react";
import { Document, Page, View, Text, Image, StyleSheet } from "@react-pdf/renderer";

const COLORS = {
  black: "#050505",
  gold: "#C9A95A",
  green: "#1F4E3D",
  red: "#C05C47",
  amber: "#8C6B2F",
  textMuted: "#575A57",
  border: "#D4D4D0",
  panelBg: "#FAFAF7",
};

const s = StyleSheet.create({
  page: { padding: 36, fontFamily: "Helvetica", fontSize: 10, color: COLORS.black, lineHeight: 1.5 },

  // Header
  header: { flexDirection: "row", alignItems: "center", gap: 12, paddingBottom: 12, borderBottomWidth: 1, borderBottomColor: COLORS.gold, marginBottom: 16 },
  logo: { width: 48, height: 48, objectFit: "contain" },
  headerText: { flex: 1 },
  brandLine: { fontSize: 16, fontFamily: "Helvetica-Bold", color: COLORS.black },
  subTitle: { fontSize: 9, color: COLORS.textMuted, marginTop: 2, letterSpacing: 1, textTransform: "uppercase" },
  versionBox: { fontSize: 8, color: COLORS.textMuted },

  // Titre de page
  pageTitle: { fontSize: 20, fontFamily: "Helvetica-Bold", color: COLORS.green, marginBottom: 4 },
  pageSubtitle: { fontSize: 10, color: COLORS.textMuted, marginBottom: 18 },

  // Section
  sectionTitle: { fontSize: 13, fontFamily: "Helvetica-Bold", color: COLORS.black, marginTop: 14, marginBottom: 6, borderBottomWidth: 0.5, borderBottomColor: COLORS.border, paddingBottom: 3 },
  sectionIntro: { fontSize: 10, color: COLORS.textMuted, marginBottom: 8 },

  // Steps
  step: { flexDirection: "row", gap: 8, marginBottom: 8 },
  stepNumber: { width: 22, height: 22, borderRadius: 11, backgroundColor: COLORS.green, color: "white", fontFamily: "Helvetica-Bold", fontSize: 11, textAlign: "center", paddingTop: 4 },
  stepBody: { flex: 1, paddingTop: 2 },
  stepTitle: { fontSize: 11, fontFamily: "Helvetica-Bold", color: COLORS.black, marginBottom: 2 },
  stepText: { fontSize: 9.5, color: COLORS.textMuted },

  // Info box
  infoBox: { borderWidth: 1, borderColor: COLORS.border, backgroundColor: COLORS.panelBg, borderRadius: 4, padding: 10, marginVertical: 6 },
  infoTitle: { fontSize: 10, fontFamily: "Helvetica-Bold", marginBottom: 4 },
  infoText: { fontSize: 9.5, color: COLORS.textMuted },

  // Reason cards (5 motifs de retrait)
  reasonGrid: { flexDirection: "row", flexWrap: "wrap", gap: 6, marginTop: 4 },
  reasonCard: { width: "32%", borderWidth: 1, borderColor: COLORS.border, borderRadius: 4, padding: 6, backgroundColor: "white" },
  reasonLabel: { fontSize: 9, fontFamily: "Helvetica-Bold" },
  reasonDesc: { fontSize: 8, color: COLORS.textMuted, marginTop: 2, lineHeight: 1.3 },
  reasonType: { fontSize: 7, marginTop: 3, fontFamily: "Helvetica-Bold" },

  // Footer
  footer: { position: "absolute", bottom: 20, left: 36, right: 36, paddingTop: 6, borderTopWidth: 0.5, borderTopColor: COLORS.border, flexDirection: "row", justifyContent: "space-between", fontSize: 8, color: COLORS.textMuted },

  // Checklist
  checkRow: { flexDirection: "row", gap: 6, marginVertical: 2 },
  checkBox: { width: 10, height: 10, borderWidth: 1, borderColor: COLORS.black, marginTop: 1 },
  checkLabel: { fontSize: 9.5, flex: 1 },

  // Tip
  tipBox: { borderLeftWidth: 3, borderLeftColor: COLORS.gold, paddingLeft: 8, marginVertical: 6 },
  tipLabel: { fontSize: 8, fontFamily: "Helvetica-Bold", color: COLORS.gold, textTransform: "uppercase", letterSpacing: 1 },
  tipText: { fontSize: 9.5, color: COLORS.textMuted, marginTop: 2 },
});

const dateStr = new Date().toLocaleDateString("fr-CH", { day: "2-digit", month: "long", year: "numeric" });

function Header({ logoUrl }) {
  return (
    <View style={s.header} fixed>
      {logoUrl ? <Image src={logoUrl} style={s.logo} /> : null}
      <View style={s.headerText}>
        <Text style={s.brandLine}>Maison Thaï Conthey</Text>
        <Text style={s.subTitle}>Procédure tournée terrain</Text>
      </View>
      <View>
        <Text style={s.versionBox}>v1 · {dateStr}</Text>
      </View>
    </View>
  );
}

function Footer() {
  return (
    <View style={s.footer} fixed>
      <Text>Maison Thaï Conthey · Rue de Parcouret 22 · CH-1964 Conthey</Text>
      <Text render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} />
    </View>
  );
}

export default function FieldOpsGuidePdf({ logoUrl }) {
  return (
    <Document>
      {/* ============== PAGE 1 — Vue d'ensemble ============== */}
      <Page size="A4" style={s.page}>
        <Header logoUrl={logoUrl} />

        <Text style={s.pageTitle}>Procédure tournée matin</Text>
        <Text style={s.pageSubtitle}>
          Guide pratique pour les opérateurs terrain — visite distributeurs &amp; revendeurs.
        </Text>

        {/* But */}
        <View style={s.infoBox}>
          <Text style={s.infoTitle}>À quoi ça sert</Text>
          <Text style={s.infoText}>
            Chaque matin, vous passez chez nos distributeurs et revendeurs. Cette procédure vous permet de :
            (1) compter le stock réel, (2) retirer les barquettes en perte avec un motif clair,
            (3) réapprovisionner avec la marchandise du sac, (4) garantir la traçabilité HACCP.
          </Text>
        </View>

        {/* Avant de partir */}
        <Text style={s.sectionTitle}>Avant de partir — Checklist</Text>
        <View style={s.checkRow}><View style={s.checkBox} /><Text style={s.checkLabel}>Tablette chargée, 4G/Wi-Fi actif</Text></View>
        <View style={s.checkRow}><View style={s.checkBox} /><Text style={s.checkLabel}>Connexion AgroManage testée (login admin@maison-thai.pro ou compte opérateur)</Text></View>
        <View style={s.checkRow}><View style={s.checkBox} /><Text style={s.checkLabel}>Lecture des « Tâches du jour » via /distributors/field-tasks</Text></View>
        <View style={s.checkRow}><View style={s.checkBox} /><Text style={s.checkLabel}>Brief matinal reçu par email (résumé DLC + alertes stock)</Text></View>
        <View style={s.checkRow}><View style={s.checkBox} /><Text style={s.checkLabel}>Marchandise dans le sac réfrigéré (≤ 5°C) selon les besoins identifiés</Text></View>
        <View style={s.checkRow}><View style={s.checkBox} /><Text style={s.checkLabel}>Étiquettes imprimées (lots du jour si production matin)</Text></View>

        {/* Étapes terrain */}
        <Text style={s.sectionTitle}>Sur place — 4 étapes par distributeur</Text>

        <View style={s.step}>
          <Text style={s.stepNumber}>1</Text>
          <View style={s.stepBody}>
            <Text style={s.stepTitle}>Ouvrir le scan terrain</Text>
            <Text style={s.stepText}>
              Sur la tablette, allez sur <Text style={{ fontFamily: "Helvetica-Bold" }}>maison-thai.ch/scan</Text>
              (ou tapez sur l&apos;icône Maison Thaï depuis l&apos;écran d&apos;accueil).
              Sélectionnez le distributeur où vous êtes.
            </Text>
          </View>
        </View>

        <View style={s.step}>
          <Text style={s.stepNumber}>2</Text>
          <View style={s.stepBody}>
            <Text style={s.stepTitle}>Compter le stock réel visuellement</Text>
            <Text style={s.stepText}>
              Pour chaque référence présente, comparez ce que vous VOYEZ dans le distributeur
              au stock indiqué dans l&apos;app. Si l&apos;écart est dû à une vente normale : ne touchez à rien
              (la vente sera tracée via le CSV Nayax ou la déduction automatique).
              Si l&apos;écart est dû à une perte/casse/blocage : passez à l&apos;étape 3.
            </Text>
          </View>
        </View>

        <View style={s.step}>
          <Text style={s.stepNumber}>3</Text>
          <View style={s.stepBody}>
            <Text style={s.stepTitle}>Retirer les barquettes problématiques avec motif</Text>
            <Text style={s.stepText}>
              Tapez sur l&apos;icône poubelle de la barquette concernée. Choisissez le bon motif
              parmi les 5 ci-dessous (voir page 2). Ne JAMAIS supprimer sans motif —
              cela casse la traçabilité HACCP.
            </Text>
          </View>
        </View>

        <View style={s.step}>
          <Text style={s.stepNumber}>4</Text>
          <View style={s.stepBody}>
            <Text style={s.stepTitle}>Réapprovisionner depuis le sac</Text>
            <Text style={s.stepText}>
              Scannez l&apos;EAN13 de chaque barquette que vous mettez en rayon/machine.
              L&apos;app les associe au bon lot et les place dans le distributeur courant.
              Vérifiez que la DLC reste ≥ 7 jours après mise en rayon.
            </Text>
          </View>
        </View>

        <View style={s.tipBox}>
          <Text style={s.tipLabel}>Astuce</Text>
          <Text style={s.tipText}>
            Si la tablette n&apos;a pas de réseau, l&apos;app accumule les actions et synchronise dès retour en zone Wi-Fi.
            Évitez de quitter l&apos;app entre 2 distributeurs.
          </Text>
        </View>

        <Footer />
      </Page>

      {/* ============== PAGE 2 — Les 5 motifs ============== */}
      <Page size="A4" style={s.page}>
        <Header logoUrl={logoUrl} />

        <Text style={s.pageTitle}>Les 5 motifs de retrait</Text>
        <Text style={s.pageSubtitle}>
          Tout retrait de stock doit choisir un motif. Ne jamais supprimer « dans le vide ».
        </Text>

        <View style={s.reasonGrid}>
          <View style={s.reasonCard}>
            <Text style={[s.reasonLabel, { color: COLORS.red }]}>1. Perte DLC</Text>
            <Text style={s.reasonDesc}>DLC dépassée. Barquette à jeter en poubelle organique.</Text>
            <Text style={[s.reasonType, { color: COLORS.red }]}>→ OUT</Text>
          </View>

          <View style={s.reasonCard}>
            <Text style={[s.reasonLabel, { color: COLORS.red }]}>2. Casse / rebut</Text>
            <Text style={s.reasonDesc}>Barquette cassée, abîmée, defectueuse. Notes obligatoires (chute, défaut…).</Text>
            <Text style={[s.reasonType, { color: COLORS.red }]}>→ OUT</Text>
          </View>

          <View style={s.reasonCard}>
            <Text style={[s.reasonLabel, { color: COLORS.amber }]}>3. Bloquée en machine</Text>
            <Text style={s.reasonDesc}>Coincée dans le distributeur ou inaccessible. Reporté par le revendeur.</Text>
            <Text style={[s.reasonType, { color: COLORS.amber }]}>→ OUT</Text>
          </View>

          <View style={s.reasonCard}>
            <Text style={[s.reasonLabel, { color: COLORS.green }]}>4. Retour Conthey</Text>
            <Text style={s.reasonDesc}>Renvoi vers l&apos;entrepôt central pour redistribution.</Text>
            <Text style={[s.reasonType, { color: COLORS.green }]}>→ TRANSFER</Text>
          </View>

          <View style={s.reasonCard}>
            <Text style={[s.reasonLabel, { color: COLORS.green }]}>5. Transfert vers…</Text>
            <Text style={s.reasonDesc}>Déplacement vers un autre distributeur/revendeur. Destination à préciser.</Text>
            <Text style={[s.reasonType, { color: COLORS.green }]}>→ TRANSFER</Text>
          </View>
        </View>

        <Text style={s.sectionTitle}>Cas particuliers</Text>

        <View style={s.infoBox}>
          <Text style={s.infoTitle}>Le revendeur a reçu un mauvais produit (erreur de BL)</Text>
          <Text style={s.infoText}>
            Ne touchez PAS au stock du revendeur. Notez l&apos;erreur. Le bureau utilisera la fonction
            « Corriger la livraison » sur le BL pour récupérer le stock erroné vers Conthey
            et générer un BL correctif. Ne supprimez jamais les lignes du BL original.
          </Text>
        </View>

        <View style={s.infoBox}>
          <Text style={s.infoTitle}>DLC périme demain ou aujourd&apos;hui</Text>
          <Text style={s.infoText}>
            Action immédiate : retrait avec motif <Text style={{ fontFamily: "Helvetica-Bold" }}>« Perte DLC »</Text>
            si périmée, ou <Text style={{ fontFamily: "Helvetica-Bold" }}>« Retour Conthey »</Text> si encore consommable
            mais à risque (revente solidaire ou destruction côté bureau).
          </Text>
        </View>

        <View style={s.infoBox}>
          <Text style={s.infoTitle}>Le client veut acheter directement à la machine sans Nayax</Text>
          <Text style={s.infoText}>
            Encaissez en espèces. À la fin de la tournée, créez une vente manuelle dans
            <Text style={{ fontFamily: "Helvetica-Bold" }}> /ventes</Text>
            avec le moyen de paiement « Espèces ». Source = MANUAL.
          </Text>
        </View>

        <Text style={s.sectionTitle}>Règles d&apos;or</Text>
        <View style={s.checkRow}><Text style={s.checkLabel}>• <Text style={{ fontFamily: "Helvetica-Bold" }}>Jamais</Text> de suppression sans motif.</Text></View>
        <View style={s.checkRow}><Text style={s.checkLabel}>• Toujours respecter la chaîne du froid <Text style={{ fontFamily: "Helvetica-Bold" }}>≤ 5°C</Text> (sac réfrigéré pendant la tournée).</Text></View>
        <View style={s.checkRow}><Text style={s.checkLabel}>• En cas de doute, <Text style={{ fontFamily: "Helvetica-Bold" }}>appeler le bureau</Text> (+41 27 346 07 75) plutôt que d&apos;improviser.</Text></View>
        <View style={s.checkRow}><Text style={s.checkLabel}>• Fin de tournée : vérifier que le sac réfrigéré est <Text style={{ fontFamily: "Helvetica-Bold" }}>vide ou retourné à Conthey</Text>.</Text></View>

        <Footer />
      </Page>

      {/* ============== PAGE 3 — Import CSV Nayax (admin bureau) ============== */}
      <Page size="A4" style={s.page}>
        <Header logoUrl={logoUrl} />

        <Text style={s.pageTitle}>Côté bureau — Import CSV Nayax matinal</Text>
        <Text style={s.pageSubtitle}>
          Tant que l&apos;API Nayax n&apos;est pas activée, on récupère les ventes via export CSV manuel.
        </Text>

        <Text style={s.sectionTitle}>Procédure matinale (5 min)</Text>

        <View style={s.step}>
          <Text style={s.stepNumber}>1</Text>
          <View style={s.stepBody}>
            <Text style={s.stepTitle}>Se connecter au Nayax Manager</Text>
            <Text style={s.stepText}>
              URL : <Text style={{ fontFamily: "Helvetica-Bold" }}>manager.nayax.com</Text>.
              Identifiants partagés avec le responsable Conthey.
            </Text>
          </View>
        </View>

        <View style={s.step}>
          <Text style={s.stepNumber}>2</Text>
          <View style={s.stepBody}>
            <Text style={s.stepTitle}>Aller dans Reports → Sales by Machine</Text>
            <Text style={s.stepText}>
              Filtrer sur la journée d&apos;hier (ou depuis le dernier import).
              Vérifier qu&apos;il y a au moins 1 ligne avant d&apos;exporter.
            </Text>
          </View>
        </View>

        <View style={s.step}>
          <Text style={s.stepNumber}>3</Text>
          <View style={s.stepBody}>
            <Text style={s.stepTitle}>Cliquer sur Export → CSV</Text>
            <Text style={s.stepText}>
              Le fichier se télécharge automatiquement. Nom typique : <Text style={{ fontFamily: "Helvetica-Bold" }}>SalesReport_YYYYMMDD.csv</Text>.
              Encoding UTF-8 ou CP1252 supporté.
            </Text>
          </View>
        </View>

        <View style={s.step}>
          <Text style={s.stepNumber}>4</Text>
          <View style={s.stepBody}>
            <Text style={s.stepTitle}>Importer dans AgroManage</Text>
            <Text style={s.stepText}>
              Allez dans <Text style={{ fontFamily: "Helvetica-Bold" }}>Paramètres → Import CSV Nayax</Text>.
              Glissez le fichier. L&apos;app détecte automatiquement les colonnes en FR/DE/EN.
              Cliquez <Text style={{ fontFamily: "Helvetica-Bold" }}>« Simulation »</Text> d&apos;abord pour vérifier les erreurs,
              puis <Text style={{ fontFamily: "Helvetica-Bold" }}>« Importer pour de vrai »</Text>.
            </Text>
          </View>
        </View>

        <View style={s.tipBox}>
          <Text style={s.tipLabel}>Dedup automatique</Text>
          <Text style={s.tipText}>
            L&apos;app garde une trace de chaque ID Transaction Nayax. Vous pouvez réimporter le même
            CSV sans risque — les doublons sont ignorés silencieusement. Pratique si vous doutez d&apos;avoir oublié un jour.
          </Text>
        </View>

        <Text style={s.sectionTitle}>En cas d&apos;erreurs d&apos;import</Text>

        <View style={s.infoBox}>
          <Text style={s.infoTitle}>« Machine Nayax non mappée »</Text>
          <Text style={s.infoText}>
            Allez dans <Text style={{ fontFamily: "Helvetica-Bold" }}>Distributeurs</Text> → ouvrez le distributeur concerné →
            renseignez le champ <Text style={{ fontFamily: "Helvetica-Bold" }}>« ID Machine Nayax »</Text> avec la valeur
            visible sur Nayax Manager (Machines → cliquez sur la machine → Machine ID).
          </Text>
        </View>

        <View style={s.infoBox}>
          <Text style={s.infoTitle}>« Produit introuvable »</Text>
          <Text style={s.infoText}>
            Le nom dans le CSV ne correspond à aucun produit AgroManage. Soit le nom Nayax est différent
            (modifiez-le côté Nayax pour matcher), soit créez le produit manquant côté AgroManage.
          </Text>
        </View>

        <View style={s.infoBox}>
          <Text style={s.infoTitle}>« Stock insuffisant »</Text>
          <Text style={s.infoText}>
            Le distributeur a vendu une barquette qui n&apos;était pas en stock côté AgroManage. Vérifiez l&apos;historique
            des réapprovisionnements terrain — il manque probablement une saisie de scan-appro.
          </Text>
        </View>

        <Footer />
      </Page>
    </Document>
  );
}
